<template>
  <div>
    <v-card flat class="mb-4">
      <v-card-text>
        <v-card flat class="mb-4">
          <v-card-subtitle class="px-0 pt-0 subtitle-1 font-weight-bold">
            <v-icon left color="deep-orange">mdi-car-emergency</v-icon>
            Equipo básico de emergencia
          </v-card-subtitle>
          <v-divider class="mb-4"></v-divider>
          <v-row>
            <v-col cols="12" v-if="isLoadingEquip">
              <v-progress-linear indeterminate color="primary" />
            </v-col>
            <v-col cols="12" v-if="loadError">
              <v-alert type="error" outlined>{{ loadError }}</v-alert>
            </v-col>
            <template v-else>
              <template v-if="equipments.length === 0">
                <v-col cols="12">
                  <v-alert type="info" outlined class="text-center">
                    No tiene equipamiento activo en la configuración.
                  </v-alert>
                </v-col>
              </template>
              <template v-else>
                <v-col v-for="eq in equipments" :key="eq.id" cols="12" md="4">
                  <v-hover v-slot="{ hover }">
                    <v-card
                      :elevation="hover ? 4 : 1"
                      class="pa-4 text-center transition-swing"
                      :class="{ 'equipment-selected': isEquipmentSelected(eq.name) }"
                      @click="toggleEquipment(eq)"
                    >
                      <v-icon size="48" :color="getEquipmentIconColor(eq)">
                        {{ getEquipmentIcon(eq.name) }}
                      </v-icon>
                      <div class="subtitle-2 mt-2">{{ getEquipmentLabel(eq.name) }}</div>
                    </v-card>
                  </v-hover>
                </v-col>
              </template>
            </template>
          </v-row>
        </v-card>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import EquipmentService from '@/services/EquipmentService'

export default {
  name: 'EquipmentStepBlock',
  props: {
    vehicle: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    emergencyLight: {
      type: Boolean,
      default: false
    },
    emergencyTriangle: {
      type: Boolean,
      default: false
    },
    emergencyVest: {
      type: Boolean,
      default: false
    },
    documentation: {
      type: Boolean,
      default: false
    },
    chargingCable: {
      type: Boolean,
      default: false
    },
    // Objeto con equipamientos seleccionados que puede venir del padre
    selectedEquipmentsFromParent: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      equipments: [],
      isLoadingEquip: false,
      loadError: null,
      selectedEquipments: {},
      // Mapeo de iconos conocidos (puedes expandir esta lista)
      iconMapping: {
        'baliza_v16': 'mdi-car-light-high',
        'triangulo': 'mdi-triangle-outline',
        'chalecos': 'mdi-tshirt-crew',
        'documentacion': 'mdi-file-document-outline',
        'cable_carga': 'mdi-ev-plug-type2'
      },
      // Mapeo de labels conocidos (puedes expandir esta lista)
      // N-06: mantener en sincronía con equipmentDisplayName en Notificaciones.vue, AppNotifications.vue, MovimientoDetail.vue y CesionDetail.vue.
      labelMapping: {
        'baliza_v16': 'Baliza V16',
        'triangulo': 'Triángulo de emergencia',
        'chalecos': 'Chaleco reflectante',
        'documentacion': 'Documentación',
        'cable_carga': 'Cable de carga',
        'gato': 'Gato'
      }
    }
  },
  computed: {
    vehicleCondition() {
      if (!this.vehicle || !this.vehicle.condition) return 'nuevo'
      const cond = this.vehicle.condition.toLowerCase()
      if (cond === 'new') return 'nuevo'
      if (cond === 'used') return 'usado'
      return cond
    },
    vehicleType() {
      if (!this.vehicle || !this.vehicle.fuel) return 'gasolina_diesel_hibrido'
      const fuelRaw = String(this.vehicle.fuel)
      const fuel = fuelRaw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

      if (['gasolina', 'diesel', 'hibrido'].includes(fuel)) return 'gasolina_diesel_hibrido'
      if (fuel === 'hibrido enchufable') return 'hibrido_enchufable'
      if (fuel === 'electrico') return 'electrico'
      return 'gasolina_diesel_hibrido'
    },
    selectedEquipmentsWithIds() {
      const result = {}
      this.equipments.forEach(eq => {
        result[eq.name] = {
          selected: this.selectedEquipments[eq.name] || false,
          id: eq.id
        }
      })
      return result
    }
  },
  methods: {
    async loadEquipments() {
      this.isLoadingEquip = true
      this.loadError = null
      try {
        console.log('equipmentStepBlock', this.vehicle.id)

        const res = await EquipmentService.getConfigByCombinationMovements(
          this.vehicle.dealer_id,
          this.vehicleCondition,
          'movimiento',
          this.vehicleType,
          this.vehicle.id
        )
        if (res.data && res.data.data && res.data.data.data && Array.isArray(res.data.data.data.equipments)) {
          this.equipments = res.data.data.data.equipments.filter(eq => eq.is_active)
          this.initializeSelectedEquipments()
        } else {
          this.equipments = []
        }
      } catch (e) {
        this.loadError = 'Error al cargar equipamientos.'
        this.equipments = []
      } finally {
        this.isLoadingEquip = false
      }
    },
    initializeSelectedEquipments() {
      // Preservar selectedEquipments existente (puede venir del padre)
      const existingSelected = { ...this.selectedEquipmentsFromParent }
      
      // Inicializar dinámicamente basado en los equipamientos recibidos
      this.selectedEquipments = {}
      this.equipments.forEach(equipment => {
        // Si ya existe en selectedEquipments del padre, preservar esos valores
        if (existingSelected[equipment.name]) {
          this.selectedEquipments[equipment.name] = {
            selected: existingSelected[equipment.name].selected,
            id: equipment.id
          }
        } else {
          // Si no existe, inicializar con valores por defecto
          this.selectedEquipments[equipment.name] = {
            selected: false,
            id: equipment.id
          }
        }
      })
      
      // Solo aplicar valores de props si NO hay datos existentes del padre
      if (Object.keys(existingSelected).length === 0) {
        // Aplicar valores de props si existen
        if (this.selectedEquipments['baliza_v16'] !== undefined) {
          this.selectedEquipments['baliza_v16'].selected = this.emergencyLight
        }
        if (this.selectedEquipments['triangulo'] !== undefined) {
          this.selectedEquipments['triangulo'].selected = this.emergencyTriangle
        }
        if (this.selectedEquipments['chalecos'] !== undefined) {
          this.selectedEquipments['chalecos'].selected = this.emergencyVest
        }
        if (this.selectedEquipments['documentacion'] !== undefined) {
          this.selectedEquipments['documentacion'].selected = this.documentation
        }
        if (this.selectedEquipments['cable_carga'] !== undefined) {
          this.selectedEquipments['cable_carga'].selected = this.chargingCable
        }
      }
    },
    toggleEquipment(equipment) {
      const equipmentName = equipment.name
      this.selectedEquipments[equipmentName].selected = !this.selectedEquipments[equipmentName].selected
      
      // Emitir eventos específicos para compatibilidad con props existentes
      switch (equipmentName) {
        case 'baliza_v16':
          this.$emit('update:emergencyLight', this.selectedEquipments[equipmentName].selected)
          break
        case 'triangulo':
          this.$emit('update:emergencyTriangle', this.selectedEquipments[equipmentName].selected)
          break
        case 'chalecos':
          this.$emit('update:emergencyVest', this.selectedEquipments[equipmentName].selected)
          break
        case 'documentacion':
          this.$emit('update:documentation', this.selectedEquipments[equipmentName].selected)
          break
        case 'cable_carga':
          this.$emit('update:chargingCable', this.selectedEquipments[equipmentName].selected)
          break
      }
      
      // Emitir evento genérico para equipamientos nuevos
      this.$emit('equipment-changed', {
        id: equipment.id,
        name: equipmentName,
        selected: this.selectedEquipments[equipmentName].selected,
        equipment: equipment
      })
      
      // Emitir evento con todos los equipamientos seleccionados
      this.$emit('update:selectedEquipments', this.selectedEquipments)
    },
    isEquipmentSelected(equipmentName) {
      return this.selectedEquipments[equipmentName] ? this.selectedEquipments[equipmentName].selected : false
    },
    getEquipmentIcon(name) {
      return this.iconMapping[name] || 'mdi-cog'
    },
    getEquipmentLabel(name) {
      return this.labelMapping[name] || this.formatLabel(name)
    },
    formatLabel(name) {
      // Convertir nombre a label legible
      return name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase())
    },
    getEquipmentIconColor(eq) {
  const name = eq.name
  if (!eq.is_active) return 'grey'
  return this.isEquipmentSelected(name) ? 'primary' : 'grey'
    }
  },
  mounted() {
    this.loadEquipments()
  },
  watch: {
    vehicle: {
      handler() {
        this.loadEquipments()
      },
      deep: true
    },
    emergencyLight(newVal) {
      if (this.selectedEquipments['baliza_v16']) {
        this.selectedEquipments['baliza_v16'].selected = newVal
      }
    },
    emergencyTriangle(newVal) {
      if (this.selectedEquipments['triangulo']) {
        this.selectedEquipments['triangulo'].selected = newVal
      }
    },
    emergencyVest(newVal) {
      if (this.selectedEquipments['chalecos']) {
        this.selectedEquipments['chalecos'].selected = newVal
      }
    },
    documentation(newVal) {
      if (this.selectedEquipments['documentacion']) {
        this.selectedEquipments['documentacion'].selected = newVal
      }
    },
    chargingCable(newVal) {
      if (this.selectedEquipments['cable_carga']) {
        this.selectedEquipments['cable_carga'].selected = newVal
      }
    },
    selectedEquipmentsFromParent: {
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          console.log('selectedEquipmentsFromParent changed:', newVal)
          // Re-inicializar cuando cambien los datos del padre
          this.initializeSelectedEquipments()
        }
      },
      deep: true,
      immediate: true
    }
  }
}
</script>

<style scoped>
.transition-swing {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}
.equipment-selected {
  border: 2px solid #1976d2;
  background: #e3f2fd;
}
.v-card {
  cursor: pointer;
}
</style>
